import axios from "axios";
import api from "./api";
import { alertService } from "./alertService";
import { ResponseInterface, ShipmentPrintLabel } from "../interfaces";
import { getBUConfiguration } from "./businessUnitServices";
import { BusinessUnitConfigDTO } from "../interfaces/Dtos";

const URLPrinter = process.env.REACT_APP_API_PRINTER_HOST; // For printer local --port 8000
const baseURL = process.env.REACT_APP_API_HOST;

// The local print helper is reached with raw axios, not `api`: `api` adds the
// session's Bearer token to every call, and the helper neither needs it nor
// should receive it. The header also forced a CORS preflight before every
// availability check. The request counter (fix/token-refresh-single-flight)
// instruments raw axios as well, so these calls stay counted.
const printerHelper = axios;

/**
 * Whether the local print helper answers. It says nothing about the printer
 * itself: the helper replies 200 with the device switched off, accepts the job
 * and queues it. See `qa/2026-09-15-impresion-etiquetas-lote-guion-qa.md`, 2b.
 *
 * It reports nothing on its own, on purpose. Two layers both deciding to raise
 * a toast is how a single failure produced two of them (8 October 2026): the
 * caller said "the print program is not running" and this one said "could not
 * reach the printer", for the same refused connection. The message belongs to
 * whoever asked.
 */
export const IsAvaliablePrint = async (): Promise<boolean> => {
  let result: boolean;
  try {
    const response = await printerHelper.get(`${URLPrinter}`);
    if (response.status === 200) {
      result = true;
    } else {
      result = false;
    }
  } catch (error) {
    result = false;
  }
  return result;
};

async function getLabels(
  uri: string,
  ShipmentNumber: number,
  ServiceID: number,
  BuCodeSource: string,
  CreationUser: string,
  PiecesNumber?: number[],
  LabelFormat?: number
) {
  let respose: any = [];
  await api
    .post(uri, {
      ShipmentNumber,
      ServiceID,
      BuCodeSource,
      PiecesNumber,
      CreationUser,
      FormatType : LabelFormat
    })
    .then((res) => {
      respose = res.data;
    })
    .catch((error) => {
      console.log(error);
    });
  return respose;
}

async function getShipmentLabels(
  ShipmentNumber: number,
  ServiceID: number,
  BuCodeSource: string,
  CreationUser: string,
  LabelFormat:number
): Promise<ShipmentPrintLabel> {
  return await getLabels(
    `${baseURL}/v1/Shipment/GetShipmentLabelV2`,
    ShipmentNumber,
    ServiceID,
    BuCodeSource,
    CreationUser,
    undefined,
    LabelFormat
  );
}

async function getPieceLabels(
  ShipmentNumber: number,
  ServiceID: number,
  BuCodeSource: string,
  PiecesNumber: number[],
  CreationUser: string,
  LabelFormat:number
): Promise<ShipmentPrintLabel> {
  return await getLabels(
    `${baseURL}/v1/Shipment/GetShipmentLabelV2`,
    ShipmentNumber,
    ServiceID,
    BuCodeSource,
    CreationUser,
    PiecesNumber,
    LabelFormat
  );
}

const PrintLabel = async (
  shipmentLabels: ShipmentPrintLabel,
  shipmentNumber: number,
  options?: { silent?: boolean }
): Promise<boolean> => {
  let result: boolean;
  try {
    if (shipmentLabels.stringLabel.length === 0) {
      alertService.error(
        "Guía: " + shipmentNumber,
        "No se pudo obtener ninguna etiqueta de la guía especificada."
      );
      return false;
    }
    let AvaliablePrint = await IsAvaliablePrint();
    if (!AvaliablePrint) {
      // The only place this message is raised. A caller printing a batch passes
      // silent and reports once for the whole run instead.
      if (!options?.silent) {
        alertService.error(
          "No se pudo comunicar con la impresora, verifique que el programa se este ejecutando."
        );
      }
      return false;
    }
    result = await new Promise(async (resolve, rejected) => {
      await printerHelper
        .post(`${URLPrinter}/PrintMultipleShipment`, {
          LabelFormat: shipmentLabels.labelFormat,
          StringLabel: shipmentLabels.stringLabel,
        })
        .then((res) => {
          resolve(true);
        })
        .catch((error) => {
          console.log(error);
          rejected(false);
        });
    });
  } catch (error) {
    result = false;
  }
  return result;
};

// Method to print the labels of the shipment using the external printer service
export const PrintShipmentLabel = async (
  ShipmentNumber: number,
  ServiceID: number,
  BuCodeSource: string,
  CreationUser: string,
  LabelFormat:number,
  options?: { silent?: boolean }
): Promise<boolean> => {
  const stringLabel = await getShipmentLabels(
    ShipmentNumber,
    ServiceID,
    BuCodeSource,
    CreationUser,
    LabelFormat
  );

  return await PrintLabel(stringLabel, ShipmentNumber, options);
};

// Method to print the labels of the pieces using the external printer service
export const PrintPieceLabel = async (
  ShipmentNumber: number,
  ServiceID: number,
  BuCodeSource: string,
  PiecesNumber: number[],
  CreationUser: string,
  LabelFormat:number
): Promise<boolean> => {
  const stringLabel = await getPieceLabels(
    ShipmentNumber,
    ServiceID,
    BuCodeSource,
    PiecesNumber,
    CreationUser,
    LabelFormat
  );

  return await PrintLabel(stringLabel, ShipmentNumber);
};

export const GetSettingsLabels = async (BUCodeEnviroment: string): Promise<number> => {
  let labelFormat: number = 0;
  labelFormat = await await new Promise(async (resolve, rejected) => {
    await getBUConfiguration(BUCodeEnviroment).then((buConfig: ResponseInterface<BusinessUnitConfigDTO>) => {
      if (buConfig)
        resolve(buConfig.model?.labelFormat ? 1 : 0);
    });
  });
  return labelFormat;
};


export const UpdateSettingsLabels = async (BUCodeEnviroment: string, TypeLabel: number,UserUpdate: string): Promise<ResponseInterface<boolean>> => {
  let result: ResponseInterface<boolean>;
  try {
    result = await new Promise(async (resolve, rejected) => {
      await api
        .post(`${baseURL}/BusinessUnit/UpdateSettingsLabelsBusinessUnit`,{
          BUCode : BUCodeEnviroment,
          LabelFormat: TypeLabel,
          UserUpdate: UserUpdate
        })
        .then((res) => {
          resolve(res.data.model);
        })
        .catch((error) => {
          console.log(error);
          rejected({ didError: true, message: "Error updating label settings", model: false });
        });
    });
  } catch (error) {
    result = { didError: true, message: "Error updating label settings", model: false } as ResponseInterface<boolean>;
  }
  return result;
};